"use client";

import { cn } from "@health/ui";
import { ArrowRight, Lock } from "lucide-react";
import { AuthButton } from "../auth/components/AuthButton";

type AuthInlineCardProps = {
  onOpenModal: (targetScreen: "signin" | "signup") => void;
  modalOpen?: boolean;
  /**
   * Auth gate behavior. `blocking` adds sticky-elevated framing + "Required to
   * continue" badge to make the gate visually unmissable. `dismissible` keeps
   * the existing inline-card affordance.
   *
   * Note: dismissibility is enforced at the modal layer (AuthModal close-path
   * suppression) and the server (`processAuthWidgetOutputs` rejects forged
   * cancels). This prop only controls visual emphasis.
   */
  authMode?: "blocking" | "dismissible";
};

export function AuthInlineCard({
  onOpenModal,
  modalOpen = false,
  authMode = "dismissible",
}: AuthInlineCardProps) {
  const isBlocking = authMode === "blocking";

  if (modalOpen) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-[0px_12px_20px_0px_rgba(0,0,0,0.05)] outline-1 -outline-offset-1 outline-(--color-text-primary)/5">
        <div className="flex items-center justify-center gap-2 text-(--color-text-primary)/50 text-xs">
          <span>Continue in the auth pane</span>
          <ArrowRight size={16} />
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-labelledby="auth-inline-card-title"
      aria-live={isBlocking ? "polite" : undefined}
      className={cn(
        "max-w-xl p-5 bg-white rounded-2xl flex flex-col gap-3 outline-1 -outline-offset-1",
        isBlocking
          ? "shadow-[0px_16px_32px_0px_rgba(0,0,0,0.10)] outline-(--color-accent)/30"
          : "shadow-[0px_12px_20px_0px_rgba(0,0,0,0.05)] outline-(--color-text-primary)/5",
      )}
    >
      {isBlocking && (
        <div className="inline-flex items-center gap-1.5 self-start rounded-full bg-(--color-accent)/10 px-2.5 py-0.5">
          <Lock size={12} className="text-(--color-accent)" />
          <span className="text-(--color-accent) text-[11px] font-medium leading-5 tracking-tight">
            Required to continue
          </span>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        <div className="flex flex-col">
          <div className="inline-flex items-center">
            <div
              id="auth-inline-card-title"
              className="text-(--color-text-primary) text-base font-normal leading-6"
            >
              {isBlocking ? "Quick step to save your progress" : "Create an account or sign in"}
            </div>
          </div>
          <div className="inline-flex items-center">
            <div className="text-(--color-text-primary)/50 text-xs font-normal leading-6">
              {isBlocking
                ? "We'll pick up right where we left off — your answers stay private to your provider."
                : "We'll save your progress so we can pick up right where we left off next time."}
            </div>
          </div>
        </div>

        <AuthButton
          type="button"
          variant="primary"
          icon={<Lock size={16} />}
          onClick={() => onOpenModal("signup")}
        >
          Create an account
        </AuthButton>
      </div>

      <div className="self-stretch text-center">
        <span className="text-(--color-text-primary)/50 text-xs font-normal leading-6">
          Already have an account?{" "}
        </span>
        <button
          type="button"
          onClick={() => onOpenModal("signin")}
          className="text-(--color-text-primary)/50 text-xs font-normal underline leading-6 hover:text-(--color-text-primary) transition-colors cursor-pointer"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}
